import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import FlexGrid from '@tds/core-flex-grid';
import Select from '@tds/core-select';
import Heading from '@tds/core-heading';
import DimpleDivider from '@tds/core-dimple-divider';
import history from '../../../components/history';
import request from '../../utils/request';
import { getLocale } from '../../misc';
import Form from './Form';
import PricingBar from './PricingBar';
import SubscribersList from './SubscribersList';
import CurrentSubscriberSummaryCard from './CurrentSubscriberSummaryCard';
import { wlsSubmitPayload } from '../formSubmitLogic';
import { calculateTotal } from './utils';
import { useTranslationsContext } from '../../context/TranslationsContext';
import {
  fieldSubscriberDetailsDeviceEnrolmentProgram,
  fieldSubscriberDetailsAppleCustomerID,
  fieldSubscriberDetailsSamsungCustomerID,
  fieldSubscriberDetailsDevicePlan,
  AccessoriesOnlyEnglish,
  AccessoriesOnlyFrench,
  categoryWireless,
  sevenX,
  fieldImei,
  orderIsNotEligibleForMSSFrenchMappings,
  smbOnlineOrder,
  SmbOnlineFrench
} from '../../utils/constant';
import ErrorMessage from '../../errorMessage';
import EN from '../../../EN.json';
import FR from '../../../FR.json';
import { compareNonAscii } from '../../utils';

const profile = request.getProfile();
const WirelessOrderDetails = ({
  formStepFields,
  formFields,
  setFormFields,
  formErrors,
  orderData,
  onBackClick,
  getErrors,
  setFormErrors,
  onFormSubmit
}) => {
  const translations = useTranslationsContext();
  const {
    WLS: {
      WIRELESS_ORDER_DETAILS: {
        Subscriber: {
          Subscriber,
          of,
          EditingSubscriber,
          PleaseSelectSubscriber,
          CopyFromSubscriber,
          SubscriberError
        }
      },
      SUBSCRIBER_DETAILS: { Accessories }
    }
  } = translations;
  const {
    formStep: { subscribers16Plus: subscribers16PlusEnglish }
  } = EN;

  const {
    formStep: { subscribers16Plus: subscribers16PlusFrench }
  } = FR;
  const channelWLS = profile.channel === undefined ? '' : profile.channel;
  const isChannelSevenX = channelWLS && channelWLS.name === sevenX;
  const totalSubscribers = +_.get(orderData, 'numberOfSubscribers', 1);
  const [subscriberDetails, setSubscriberDetails] = useState(
    _.get(orderData, 'subscriberDetails', [])
  );
  const isAccessoriesOnly =
    orderData.orderNotEligibleforMssReason &&
    (orderData.orderNotEligibleforMssReason.includes(AccessoriesOnlyEnglish) ||
      orderData.orderNotEligibleforMssReason.includes(AccessoriesOnlyFrench));

  // Get first subscriber whose status is isComplete = false
  const firstIncompleteSubscriber = subscriberDetails.find(
    item => !item.isCompleted
  );
  // Current subscriber should be first subscriber whose status is isComplete = false ELSE
  // if user clicks on Continue subscriber, increment by one
  const [currentSubscriber, setCurrentSubscriber] = useState(
    firstIncompleteSubscriber
      ? firstIncompleteSubscriber.subscriberNumber
      : subscriberDetails.length + 1
  );

  const [formTouched, setFormTouched] = useState(false);
  const [total, setTotal] = useState({});
  // Set editing subscriber if first subscriber whose status is isComplete = false is found ELSE
  // set it to empty
  const [editingSubscriber, setEditingSubscriber] = useState(
    firstIncompleteSubscriber && currentSubscriber <= totalSubscribers
      ? firstIncompleteSubscriber.id
      : ''
  );
  const editingSubscriberObject = subscriberDetails.find(
    item => item.id === editingSubscriber
  );
  const [selectedFeatures, setSelectedFeatures] = useState(
    (editingSubscriberObject && editingSubscriberObject.features) || []
  );
  const [selectedDeviceCare, setSelectedDeviceCare] = useState(
    (editingSubscriberObject && editingSubscriberObject.deviceCare) || []
  );
  const [selectedAccessories, setSelectedAccessories] = useState(
    (editingSubscriberObject && editingSubscriberObject.accessories) || []
  );
  const [selectedPromotions, setSelectedPromotions] = useState([]);

  useEffect(() => {
    setSelectedPromotions(
      (editingSubscriberObject && editingSubscriberObject.promotions) || []
    );
  }, [editingSubscriberObject]);

  const [editingSubscriberNumber, setEditingSubscriberNumber] = useState();
  const [
    editingPreviousSubscriberNumber,
    setEditingPreviousSubscriberNumber
  ] = useState();
  const [copyFromSubscriber, setCopyFromSubscriber] = useState();
  const [isCopyFromSubscriber, setIsCopyFromSubscriber] = useState(false);
  const [showErrorSubscriber, setShowErrorSubscriber] = useState(null);

  const resetForm = () => {
    const updatedFields = {};
    Object.keys(formFields).forEach(key => {
      updatedFields[key] = {
        ...formFields[key],
        value: ''
      };
    });
    setFormFields(updatedFields);
    setSelectedAccessories([]);
    setSelectedPromotions([]);
    setSelectedFeatures([]);
    setSelectedDeviceCare([]);
  };

  const getFormData = fields => {
    const result = Object.keys(fields)
      .filter(
        key => fields[key].isVisible && typeof fields[key].value !== 'undefined'
      )
      .reduce((acc, key) => {
        const { type, value, checked } = fields[key];
        acc[key] = type === 'Checkbox' ? checked : value;
        return acc;
      }, {});

    // Adding Field Data
    if (fields.files) result.files = fields.files;

    let updatedFields;
    const hideDetailsDeviceEnrolmentProgramFields = [
      {
        enrollmentValue: 'Samsung Knox',
        fieldsToHide: [fieldSubscriberDetailsAppleCustomerID]
      },
      {
        enrollmentValue: 'Apple Business Manager',
        fieldsToHide: [fieldSubscriberDetailsSamsungCustomerID]
      },
      {
        enrollmentValue: 'None',
        fieldsToHide: [
          fieldSubscriberDetailsSamsungCustomerID,
          fieldSubscriberDetailsAppleCustomerID
        ]
      },
      {
        enrollmentValue: 'Aucune',
        fieldsToHide: [
          fieldSubscriberDetailsSamsungCustomerID,
          fieldSubscriberDetailsAppleCustomerID
        ]
      }
    ];

    hideDetailsDeviceEnrolmentProgramFields.forEach(hItem => {
      const enrollmentValue =
        result[fieldSubscriberDetailsDeviceEnrolmentProgram];
      if (enrollmentValue === hItem.enrollmentValue) {
        hItem.fieldsToHide.forEach(fieldToHide => {
          delete result[fieldToHide];
        });
      }
    });

    if (editingSubscriber) {
      const subscriberIndex = _.findIndex(subscriberDetails, {
        id: editingSubscriber
      });
      const copySubscriberDetails = [...subscriberDetails];
      copySubscriberDetails[subscriberIndex] = {
        id: subscriberDetails[subscriberIndex].id,
        subscriberNumber: subscriberDetails[subscriberIndex].subscriberNumber,
        isCompleted: true,
        createdAt: subscriberDetails[subscriberIndex].createdAt,
        updatedAt: new Date().toISOString(),
        accessories: selectedAccessories,
        features: selectedFeatures,
        promotions: selectedPromotions,
        recurringCost: total.currentSubscriberTotal,
        oneTimeCost: total.oneTimeCost,
        ...result
      };
      updatedFields = {
        subscriberDetails: [...copySubscriberDetails]
      };
    } else {
      result.id = uuidv4();
      result.subscriberNumber = currentSubscriber;
      result.isCompleted = false;
      result.createdAt = new Date().toISOString();
      result.updatedAt = new Date().toISOString();
      result.accessories = selectedAccessories;
      result.features = selectedFeatures;
      result.deviceCare = selectedDeviceCare;
      result.promotions = selectedPromotions;
      result.recurringCost = total.currentSubscriberTotal;
      result.oneTimeCost = total.oneTimeCost;
      updatedFields = {
        subscriberDetails: [...subscriberDetails, { ...result }]
      };
    }

    return updatedFields;
  };

  const updateOrder = (data, action = 'ADD') => {
    // Change isCompleted = true because this subscriber detail has been filled completely
    data.subscriberDetails.map(item => (item.isCompleted = true));
    wlsSubmitPayload({ ...orderData, ...data }).then(response => {
      if (!editingSubscriber && action === 'ADD')
        setCurrentSubscriber(currentSubscriber + 1);
      if (!editingSubscriber && action === 'REMOVE')
        setCurrentSubscriber(currentSubscriber - 1);

      if (editingSubscriber) setEditingSubscriber('');
      setSubscriberDetails(response.data.subscriberDetails);
      resetForm();
      window.location.reload();
    });
  };

  const updateSaveExitOrder = data => {
    const locale = getLocale();
    // Change isCompleted = false because this subscriber detail is not completely filled
    data.subscriberDetails.map(item =>
      item.isCompleted ? (item.isCompleted = true) : (item.isCompleted = false)
    );
    wlsSubmitPayload({ ...orderData, ...data }).then(response => {
      setSubscriberDetails(response.data.subscriberDetails);
      resetForm();
      if (orderData && orderData.categoryName === categoryWireless) {
        history.push(`/${locale}/wireless/Wireless_Landing_Page?page=1`);
        window.location.reload();
      } else history.push('/');
    });
  };

  const getSeletedColorSKU = (subscriberDetailsDevices, selectedColor) => {
    const skuArray = subscriberDetailsDevices.sku.split('/');
    return (
      skuArray &&
      skuArray.length &&
      skuArray.filter(data => {
        return data.includes(selectedColor);
      })
    );
  };

  const getUpdateDeviceObject = mSubscriberDetails => {
    return (
      mSubscriberDetails &&
      mSubscriberDetails.map(item => {
        const tempData = { ...item };
        if (tempData.subscriberDetailsDevices) {
          tempData.subscriberDetailsDeviceName =
            tempData.subscriberDetailsDevices.device_name;
          if (tempData.subscriberDetailsColor) {
            tempData.subscriberDetailsDevices.selectedColor =
              tempData.subscriberDetailsColor;
            if (tempData.subscriberDetailsDevices.sku.split('/').length > 1) {
              tempData.subscriberDetailsDevices.selectedsku = getSeletedColorSKU(
                tempData.subscriberDetailsDevices,
                tempData.subscriberDetailsColor
              );
            } else {
              tempData.subscriberDetailsDevices.selectedsku =
                tempData.subscriberDetailsDevices.sku;
            }
          } else if (tempData.subscriberDetailsDevices) {
            tempData.subscriberDetailsDevices.selectedColor = tempData
              .subscriberDetailsDevices.colours
              ? tempData.subscriberDetailsDevices.colours
              : tempData.subscriberDetailsDevices.colors || '';
            tempData.subscriberDetailsDevices.selectedsku =
              tempData.subscriberDetailsDevices.sku || '';
          }
        }
        return tempData;
      })
    );
  };

  const onPrevious = e => {
    e.preventDefault();
    const recentSubscriber = subscriberDetails[subscriberDetails.length - 1];
    const index = subscriberDetails.indexOf(recentSubscriber);
    const subscriberId = _.get(recentSubscriber, 'id', '');
    setEditingSubscriber(subscriberId);
    setEditingPreviousSubscriberNumber(index + 1);
  };

  const onSubmit = e => {
    e.preventDefault();
    /**
     * If not editing a subscriber
     * and all subscribers are added move to the next step
     */
    if (currentSubscriber > totalSubscribers && !editingSubscriber) {
      const wlsOrderDetailsData = getFormData(formFields);
      const wlsNewOrderData = {
        subscriberDetails: wlsOrderDetailsData.subscriberDetails.slice(
          0,
          wlsOrderDetailsData.subscriberDetails.length - 1
        )
      };
      wlsNewOrderData.subscriberDetails =
        wlsNewOrderData.subscriberDetails &&
        wlsNewOrderData.subscriberDetails.length &&
        getUpdateDeviceObject(wlsNewOrderData.subscriberDetails);
      onFormSubmit(wlsNewOrderData);
      return;
    }
    const errors = getErrors(formStepFields, { ...formErrors });
    setFormErrors(errors);
    if (Object.entries(errors).length === 0) {
      const wlsOrderDetailsData = getFormData(formFields);
      if (isAccessoriesOnly) {
        wlsOrderDetailsData.subscriberDetails[
          wlsOrderDetailsData.subscriberDetails.length - 1
        ][fieldSubscriberDetailsDevicePlan] = Accessories;
      }

      updateOrder(wlsOrderDetailsData);
      setCurrentSubscriber(currentSubscriber + 1);
    } else {
      const firstError = errors[Object.keys(errors)[0]];
      const { ref } = firstError;
      if (ref && ref.current) ref.current.focus();
    }
  };

  const onSaveExit = e => {
    e.preventDefault();
    const wlsOrderDetailsData = getFormData(formFields);
    updateSaveExitOrder(wlsOrderDetailsData);
  };

  /**
   * Calculating total whenever new Feature or accessory is added
   */
  useEffect(() => {
    calculateTotal(
      translations,
      formFields,
      subscriberDetails,
      selectedAccessories,
      selectedFeatures,
      selectedDeviceCare,
      setTotal,
      editingSubscriber,
      orderData
    );
  }, [selectedFeatures, selectedAccessories, selectedDeviceCare]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentSubscriber, editingSubscriber]);

  useEffect(() => {
    if (!_.isEmpty(orderData.provinceWLS)) {
      localStorage.setItem('provinceWLS', orderData.provinceWLS);
    }
  }, []);

  const handleCopyFromSubscriber = () => {
    if (copyFromSubscriber) {
      // if subscriber is selected, then trigger copy subscriber prop for Form component
      setIsCopyFromSubscriber(true);
    } else {
      // else trigger show error subscriber prop for ErrorMessage component
      setShowErrorSubscriber(true);
    }
  };

  const handleSubscriberDropdown = e => {
    const subscriberId = e.currentTarget.value;
    const currSubscriber = subscriberDetails.find(
      subscriber => subscriber.id === subscriberId
    );
    setCopyFromSubscriber(currSubscriber);
    setShowErrorSubscriber(false);
  };

  // logic to show dropdown and copy button if user is at step-1 and selects "Order not eligible
  // for MSS reason" option and then selects option "Number of subscribers ordered is 16+"

  const areSubscribers16Plus =
    compareNonAscii(
      orderData.orderNotEligibleforMssReason,
      subscribers16PlusEnglish
    ) ||
    compareNonAscii(
      orderData.orderNotEligibleforMssReason,
      subscribers16PlusFrench
    );

  return (
    <>
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col>
            {editingPreviousSubscriberNumber && (
              <div style={{ margin: '1rem 0' }}>
                <Heading level="h2">
                  {EditingSubscriber} # {editingPreviousSubscriberNumber}
                </Heading>
              </div>
            )}
            {editingSubscriber &&
              !firstIncompleteSubscriber &&
              !editingPreviousSubscriberNumber && (
                <div style={{ margin: '1rem 0' }}>
                  <Heading level="h2">
                    {EditingSubscriber} # {editingSubscriberNumber}
                  </Heading>
                </div>
              )}
            {currentSubscriber <= totalSubscribers && !editingSubscriber && (
              <div style={{ margin: '1rem 0' }}>
                <Heading level="h2">
                  {Subscriber} {currentSubscriber} {of} {totalSubscribers}
                </Heading>
              </div>
            )}
            {currentSubscriber <= totalSubscribers &&
              firstIncompleteSubscriber && (
                <div style={{ margin: '1rem 0' }}>
                  <Heading level="h2">
                    {Subscriber} {currentSubscriber} {of} {totalSubscribers}
                  </Heading>
                </div>
              )}
          </FlexGrid.Col>
          <FlexGrid.Col>
            {(currentSubscriber <= totalSubscribers || totalSubscribers < 2) &&
              !editingPreviousSubscriberNumber &&
              !editingSubscriber &&
              totalSubscribers > 1 &&
              (areSubscribers16Plus || isChannelSevenX) && (
                <FlexGrid.Row>
                  <Select
                    label=" "
                    placeholder={PleaseSelectSubscriber}
                    options={subscriberDetails.map(item => {
                      return {
                        text: `${item.subscriberDetailsFirstName} ${item.subscriberDetailsLastName}`,
                        value: item.id
                      };
                    })}
                    onChange={handleSubscriberDropdown}
                  />
                  <button
                    type="button"
                    onClick={handleCopyFromSubscriber}
                    className="frg-button color-green"
                    style={{ height: 30, margin: 5 }}
                    disabled={subscriberDetails.length <= 0}
                  >
                    <span>{CopyFromSubscriber}</span>
                  </button>
                </FlexGrid.Row>
              )}
            <div style={{ marginLeft: '5.5%', width: '83%' }}>
              <ErrorMessage
                text={SubscriberError}
                isError={showErrorSubscriber}
              />
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>

        <div
          style={{
            marginBottom: '10rem',
            border: '1px solid rgb(227, 227, 227)',
            boxShadow: 'rgb(227 227 227) 1px 1px 9px 4px',
            padding: '2rem'
          }}
        >
          {(currentSubscriber <= totalSubscribers || editingSubscriber) && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ width: '70%', paddingRight: '3rem' }}>
                <Form
                  formStepFields={formStepFields}
                  formFields={formFields}
                  setFormFields={setFormFields}
                  onFormSubmit={onSubmit}
                  setFormTouched={setFormTouched}
                  formTouched={formTouched}
                  formErrors={formErrors}
                  setFormErrors={setFormErrors}
                  selectedFeatures={selectedFeatures}
                  setSelectedFeatures={data => {
                    setSelectedFeatures(data);
                  }}
                  selectedDeviceCare={selectedDeviceCare}
                  setSelectedDeviceCare={data => {
                    setSelectedDeviceCare(data);
                  }}
                  selectedAccessories={selectedAccessories}
                  setSelectedAccessories={data => {
                    setSelectedAccessories(data);
                  }}
                  selectedPromotions={selectedPromotions}
                  setSelectedPromotions={data => {
                    setSelectedPromotions(data);
                  }}
                  totalSubscribers={totalSubscribers}
                  setTotal={setTotal}
                  total={total}
                  subscriberDetails={subscriberDetails}
                  editingSubscriber={editingSubscriber}
                  setSubscriberDetails={setSubscriberDetails}
                  setEditingSubscriberNumber={setEditingSubscriberNumber}
                  orderData={orderData}
                  isCopyFromSubscriber={isCopyFromSubscriber}
                  setIsCopyFromSubscriber={setIsCopyFromSubscriber}
                  copyFromSubscriber={copyFromSubscriber}
                />
              </div>
              <CurrentSubscriberSummaryCard total={total} />
            </div>
          )}

          {subscriberDetails.length > 0 &&
            !(editingSubscriber && subscriberDetails.length === 1) && (
              <>
                <DimpleDivider />
                <SubscribersList
                  subscriberDetails={subscriberDetails}
                  setSubscriberDetails={setSubscriberDetails}
                  updateOrder={updateOrder}
                  editingSubscriber={editingSubscriber}
                  setEditingSubscriber={setEditingSubscriber}
                  currentSubscriber={currentSubscriber}
                  totalSubscribers={totalSubscribers}
                  setShowErrorSubscriber={setShowErrorSubscriber}
                />
              </>
            )}
        </div>

        <PricingBar
          formFields={formFields}
          isFormTouched={formTouched}
          onPreviousClick={onPrevious}
          onProceedClick={onSubmit}
          onBackClick={onBackClick}
          currentSubscriber={currentSubscriber}
          totalSubscribers={totalSubscribers}
          total={total.grandTotal}
          editingSubscriber={editingSubscriber}
          firstIncompleteSubscriber={firstIncompleteSubscriber}
          onSaveAndExit={onSaveExit}
        />
      </FlexGrid>
    </>
  );
};

WirelessOrderDetails.propTypes = {
  formStepFields: PropTypes.objectOf(PropTypes.any).isRequired,
  formFields: PropTypes.objectOf(PropTypes.any).isRequired,
  setFormFields: PropTypes.func.isRequired,
  orderData: PropTypes.objectOf(PropTypes.any).isRequired,
  formErrors: PropTypes.objectOf(PropTypes.any).isRequired,
  onBackClick: PropTypes.func.isRequired,
  getErrors: PropTypes.objectOf(PropTypes.any).isRequired,
  setFormErrors: PropTypes.func.isRequired,
  onFormSubmit: PropTypes.func.isRequired
};

export default WirelessOrderDetails;
